<template>
  <div class="def-entry">
    <template v-for="(item, i) in classified" :key="i">
      <!-- Lemma record: the headword's own dictionary entry -->
      <section v-if="item.kind === 'lemma'" class="def-lemma">
        <p v-if="item.data.etymology_text" class="def-etym">{{ item.data.etymology_text }}</p>

        <p v-if="ipaText(item.data)" class="def-ipa">
          <span class="def-meta-label">{{ STRINGS.definitions.pronunciation }}</span>
          <span class="def-ipa-value">{{ ipaText(item.data) }}</span>
        </p>

        <p v-if="formsText(item.data)" class="def-forms">
          <span class="def-meta-label">{{ STRINGS.definitions.forms }}</span>
          <span>{{ formsText(item.data) }}</span>
        </p>

        <div v-for="(block, bi) in item.data.pos_blocks" :key="bi" class="def-pos">
          <p class="def-pos-label">{{ block.pos || STRINGS.definitions.unknownPos }}</p>
          <ol class="def-senses">
            <li v-for="(sense, si) in block.senses" :key="si" class="def-sense">
              <p class="def-definition">{{ sense.definition }}</p>

              <p v-if="crossRef(sense.form_of)" class="def-crossref">
                <span class="def-crossref-label">{{ STRINGS.definitions.formOf }}</span>
                <span>{{ crossRef(sense.form_of) }}</span>
              </p>
              <p v-if="crossRef(sense.alt_of)" class="def-crossref">
                <span class="def-crossref-label">{{ STRINGS.definitions.altOf }}</span>
                <span>{{ crossRef(sense.alt_of) }}</span>
              </p>

              <ul v-if="sense.examples && sense.examples.length" class="def-examples">
                <li v-for="(ex, ei) in sense.examples" :key="ei" class="def-example">{{ ex }}</li>
              </ul>
            </li>
          </ol>
        </div>
      </section>

      <!-- Reference record: an inflection-only word pointing at parent lemmas -->
      <section v-else-if="item.kind === 'reference'" class="def-reference">
        <ul class="def-formof-list">
          <li v-for="(ref, ri) in item.data.form_of" :key="ri" class="def-formof">
            {{ ref.label }}
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import STRINGS from '@/constants/strings';
import {
  isLemmaRecord,
  isReferenceRecord,
  type LemmaRecord,
  type ReferenceRecord,
} from '@/types/word';

const props = defineProps<{
  records: readonly unknown[];
}>();

type Classified =
  | { kind: 'lemma'; data: LemmaRecord }
  | { kind: 'reference'; data: ReferenceRecord }
  | { kind: 'unknown' };

// Records are dataset-owned JSON (#332); classify by the pos_blocks discriminator
// and drop anything that matches neither shape rather than risk a render error.
const classified = computed<Classified[]>(() =>
  props.records.map(record => {
    if (isLemmaRecord(record)) return { kind: 'lemma', data: record };
    if (isReferenceRecord(record)) return { kind: 'reference', data: record };
    return { kind: 'unknown' };
  })
);

function ipaText(record: LemmaRecord): string {
  return (record.ipa ?? []).filter(Boolean).join(', ');
}

function formsText(record: LemmaRecord): string {
  return (record.forms ?? [])
    .map(f => {
      const tags = f.tags && f.tags.length ? ` (${f.tags.join(', ')})` : '';
      return `${f.form}${tags}`;
    })
    .join(', ');
}

// Sense-level form_of / alt_of are plain string arrays of parent headwords.
function crossRef(words: readonly string[] | undefined): string {
  return (words ?? []).filter(Boolean).join(', ');
}
</script>

<style scoped>
.def-entry {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.def-lemma {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.def-etym {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-style: italic;
  color: var(--color-arena-text-muted);
  line-height: 1.5;
  margin: 0;
}

.def-ipa,
.def-forms {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  color: var(--color-arena-text-bright);
}

.def-ipa-value {
  font-family: var(--font-mono);
}

.def-meta-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-muted);
}

.def-pos {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.def-pos-label {
  font-family: var(--font-display);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-energy-primary);
  margin: 0;
}

.def-senses {
  list-style: decimal;
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.def-sense {
  padding-left: 0.25rem;
}

.def-definition {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  line-height: 1.55;
  margin: 0;
}

.def-crossref {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 0.25rem 0 0;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
}

.def-crossref-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-muted);
}

.def-examples {
  list-style: none;
  margin: 0.375rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-left: 2px solid var(--color-arena-border);
  padding-left: 0.75rem;
}

.def-example {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-style: italic;
  color: var(--color-arena-text-muted);
  line-height: 1.5;
}

.def-reference .def-formof-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.def-formof {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  line-height: 1.5;
}
</style>
